import { useState, useEffect, useCallback } from 'react';
import {
  PushNotificationState,
  PushNotificationHookReturn,
  NotificationPayload,
  PushSubscriptionData
} from '../types/notifications';
import {
  isPushNotificationSupported,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getExistingSubscription,
  subscriptionToJSON,
  sendSubscriptionToServer,
  showLocalNotification,
  hasNotificationPermission,
  isSubscribedToPush
} from '../utils/pushNotifications';

export function usePushNotifications(
  vapidPublicKey?: string,
  serverEndpoint?: string
): PushNotificationHookReturn {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    subscription: null,
    error: null,
    isLoading: false
  });

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Initialize push notification state
  useEffect(() => {
    const initializePushNotifications = async () => {
      setState(prev => ({ ...prev, isLoading: true }));

      try {
        // Check browser support
        const isSupported = isPushNotificationSupported();
        if (!isSupported) {
          setState(prev => ({
            ...prev,
            isSupported: false,
            error: 'Push notifications are not supported in this browser',
            isLoading: false
          }));
          return;
        }

        // Register service worker
        const swRegistration = await registerServiceWorker();
        setRegistration(swRegistration);

        // Check current permission
        const permission = Notification.permission;

        // Check if already subscribed
        const existingSubscription = await getExistingSubscription(swRegistration);
        const isSubscribed = existingSubscription !== null;

        setState(prev => ({
          ...prev,
          isSupported: true,
          permission,
          isSubscribed,
          subscription: existingSubscription,
          error: null,
          isLoading: false
        }));

      } catch (error) {
        console.error('Failed to initialize push notifications:', error);
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to initialize push notifications',
          isLoading: false
        }));
      }
    };

    initializePushNotifications();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const permission = await requestNotificationPermission();
      setState(prev => ({ 
        ...prev, 
        permission, 
        isLoading: false,
        error: permission === 'denied' ? 'Notification permission was denied' : null
      }));

      return permission === 'granted';
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to request permission';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        isLoading: false 
      }));
      return false;
    }
  }, [state.isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (providedVapidKey?: string): Promise<PushSubscription | null> => {
    const keyToUse = providedVapidKey || vapidPublicKey;
    
    if (!state.isSupported || !registration || !keyToUse) {
      setState(prev => ({ 
        ...prev, 
        error: 'Cannot subscribe: missing requirements (support, registration, or VAPID key)' 
      }));
      return null;
    }

    if (state.permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        return null;
      }
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const subscription = await subscribeToPushNotifications(registration, keyToUse);
      
      // Send subscription to server if endpoint is provided
      if (serverEndpoint) {
        const subscriptionData = subscriptionToJSON(subscription);
        await sendSubscriptionToServer(subscriptionData, serverEndpoint);
      }

      setState(prev => ({
        ...prev,
        isSubscribed: true,
        subscription,
        isLoading: false
      }));

      return subscription;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to subscribe to push notifications';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        isLoading: false 
      }));
      return null;
    }
  }, [state.isSupported, state.permission, registration, vapidPublicKey, serverEndpoint, requestPermission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!registration) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const success = await unsubscribeFromPushNotifications(registration);
      
      setState(prev => ({
        ...prev,
        isSubscribed: false,
        subscription: null,
        isLoading: false
      }));

      return success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unsubscribe from push notifications';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        isLoading: false 
      }));
      return false;
    }
  }, [registration]);

  // Send test notification
  const sendTestNotification = useCallback((payload: Partial<NotificationPayload>) => {
    if (!hasNotificationPermission()) {
      setState(prev => ({ 
        ...prev, 
        error: 'Cannot send test notification: permission not granted' 
      }));
      return;
    }

    const testPayload: NotificationPayload = {
      title: 'Test Notification',
      body: 'This is a test notification from Smorg',
      icon: '/favicon.ico',
      tag: 'test-notification',
      ...payload
    };

    showLocalNotification(testPayload);
  }, []);

  return {
    state,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}